import React, { useState } from 'react';
import { ArrowLeftIcon, SearchIcon, CheckCircleIcon, PackageIcon, WarehouseIcon, FilterIcon, Leaf, ScanLineIcon, XCircleIcon, MapPinIcon, BoxIcon, ArrowRightIcon, PackagePlusIcon, PackageXIcon, AlertCircleIcon } from 'lucide-react';
interface SpiceStorageProps {
  onBackToOverview?: () => void;
}
interface RemainingSpice {
  id: string;
  name: string;
  remaining: number;
  unit: string;
  notes: string[];
}
interface SpiceOrder {
  id: string;
  orderId: string;
  recipeName: string;
  date: Date;
  remainingSpices: RemainingSpice[];
}
export function SpiceStorage({
  onBackToOverview
}: SpiceStorageProps) {
  // Workflow states
  const [step, setStep] = useState<'operationType' | 'storageType' | 'orderList' | 'spiceList' | 'scanBox' | 'fillBox' | 'scanLocation' | 'complete'>('operationType');
  // Selection states
  const [operationType, setOperationType] = useState<'storage' | 'retrieval' | null>(null);
  const [storageType, setStorageType] = useState<'leftover' | 'new' | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<SpiceOrder | null>(null);
  const [selectedSpice, setSelectedSpice] = useState<RemainingSpice | null>(null);
  const [scannedBox, setScannedBox] = useState<string | null>(null);
  const [scannedLocation, setScannedLocation] = useState<string | null>(null);
  const [completedSpices, setCompletedSpices] = useState<string[]>([]);
  // Scan states
  const [scanCode, setScanCode] = useState('');
  const [isScanning, setIsScanning] = useState(false);
  const [scanSuccess, setScanSuccess] = useState<boolean | null>(null);
  const [errorMessage, setErrorMessage] = useState('');
  // Search and filter
  const [searchTerm, setSearchTerm] = useState('');
  // Mock data for remaining spices from previous weighing operations
  const [spiceOrders] = useState<SpiceOrder[]>([{
    id: 'SPICE001',
    orderId: '1001',
    recipeName: 'Gustoria Paella',
    date: new Date(new Date().setDate(new Date().getDate() - 1)),
    remainingSpices: [{
      id: 'S001',
      name: 'Safran',
      remaining: 0.012,
      unit: 'kg',
      notes: ['Allergene: Keine', 'Lichtgeschützt lagern', 'Temperatur unter 20°C', 'Charge: LOT-2023-45A']
    }, {
      id: 'S002',
      name: 'Paprika geräuchert',
      remaining: 0.045,
      unit: 'kg',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-32B']
    }, {
      id: 'S003',
      name: 'Kurkuma',
      remaining: 0.032,
      unit: 'kg',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-28C']
    }]
  }, {
    id: 'SPICE002',
    orderId: '1002',
    recipeName: 'Gustoria Risotto',
    date: new Date(),
    remainingSpices: [{
      id: 'S004',
      name: 'Basilikum getrocknet',
      remaining: 0.028,
      unit: 'kg',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-28C']
    }, {
      id: 'S005',
      name: 'Oregano',
      remaining: 0.019,
      unit: 'kg',
      notes: ['Allergene: Keine', 'Vor Feuchtigkeit schützen', 'Charge: LOT-2023-30D']
    }]
  }]);
  // Filter orders based on search term
  const filteredOrders = spiceOrders.filter(order => order.recipeName.toLowerCase().includes(searchTerm.toLowerCase()) || order.orderId.includes(searchTerm) || order.remainingSpices.some(spice => spice.name.toLowerCase().includes(searchTerm.toLowerCase()) || spice.id.includes(searchTerm)));
  // Handle operation type selection (Step 1)
  const handleOperationTypeSelect = (type: 'storage' | 'retrieval') => {
    setOperationType(type);
    if (type === 'storage') {
      setStep('storageType');
    } else {
      // For retrieval, we would implement a different flow
      // Currently only implementing the storage flow as per requirements
      alert('Auslagerung ist derzeit noch nicht implementiert.');
    }
  };
  // Handle storage type selection (Step 2)
  const handleStorageTypeSelect = (type: 'leftover' | 'new') => {
    setStorageType(type);
    if (type === 'leftover') {
      setStep('orderList');
    } else {
      // For new products, we would implement a different flow
      // Currently only implementing the leftover flow as per requirements
      alert('Einlagerung neuer Produkte ist derzeit noch nicht implementiert.');
    }
  };
  // Handle order selection (Step 3)
  const handleOrderSelect = (order: SpiceOrder) => {
    setSelectedOrder(order);
    setStep('spiceList');
    setCompletedSpices([]);
  };
  // Handle spice selection (Step 4)
  const handleSpiceSelect = (spice: RemainingSpice) => {
    setSelectedSpice(spice);
    setStep('scanBox');
    setScanCode('');
    setScanSuccess(null);
    setErrorMessage('');
  };
  // Handle box scanning (Step 5)
  const handleScanBox = () => {
    if (!scanCode) {
      setErrorMessage('Bitte geben Sie einen Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simulate scanning process
    setTimeout(() => {
      // For demo purposes, any code is accepted as a valid box
      setScanSuccess(true);
      setScannedBox(scanCode);
      setIsScanning(false);
      setErrorMessage('');
      // Move to next step after successful scan
      setTimeout(() => {
        setStep('fillBox');
      }, 1000);
    }, 1500);
  };
  // Handle fill box confirmation (Step 6)
  const handleFillBoxConfirm = () => {
    setStep('scanLocation');
    setScanCode('');
    setScanSuccess(null);
    setErrorMessage('');
  };
  // Handle location scanning (Step 7)
  const handleScanLocation = () => {
    if (!scanCode) {
      setErrorMessage('Bitte geben Sie einen Lagerplatz-Barcode ein oder scannen Sie ihn');
      setScanSuccess(false);
      return;
    }
    setIsScanning(true);
    // Simulate scanning process
    setTimeout(() => {
      // For demo purposes, any code starting with "L" is accepted as a valid location
      const isValidLocation = scanCode.startsWith('L') || scanCode.startsWith('l');
      setScanSuccess(isValidLocation);
      if (isValidLocation) {
        setScannedLocation(scanCode);
        setErrorMessage('');
        // Move to completion or next spice
        if (selectedOrder && selectedSpice) {
          setCompletedSpices([...completedSpices, selectedSpice.id]);
          // Check if there are more spices to process
          const remainingSpices = selectedOrder.remainingSpices.filter(spice => !completedSpices.includes(spice.id) && spice.id !== selectedSpice.id);
          if (remainingSpices.length > 0) {
            // Show completion screen briefly before moving to next spice
            setStep('complete');
            setTimeout(() => {
              setStep('spiceList');
              setSelectedSpice(null);
              setScannedBox(null);
              setScannedLocation(null);
            }, 2000);
          } else {
            // All spices processed
            setStep('complete');
            setTimeout(() => {
              // Reset and go back to order list
              setStep('orderList');
              setSelectedOrder(null);
              setSelectedSpice(null);
              setScannedBox(null);
              setScannedLocation(null);
            }, 3000);
          }
        }
      } else {
        setErrorMessage('Ungültiger Lagerplatz. Bitte stellen Sie sicher, dass der Barcode korrekt ist.');
      }
      setIsScanning(false);
    }, 1500);
  };
  // Handle back navigation
  const handleBack = () => {
    switch (step) {
      case 'storageType':
        setStep('operationType');
        setOperationType(null);
        break;
      case 'orderList':
        setStep('storageType');
        setStorageType(null);
        break;
      case 'spiceList':
        setStep('orderList');
        setSelectedOrder(null);
        break;
      case 'scanBox':
        setStep('spiceList');
        setSelectedSpice(null);
        break;
      case 'fillBox':
        setStep('scanBox');
        setScannedBox(null);
        break;
      case 'scanLocation':
        setStep('fillBox');
        break;
      default:
        if (onBackToOverview) {
          onBackToOverview();
        }
    }
  };
  return <div className="container mx-auto" data-id="element-1414">
      {/* Header */}
      <div className="mb-8" data-id="element-1415">
        <h2 className="text-2xl font-bold text-gray-800 mb-2" data-id="element-1416">Gewürzlager</h2>
        <div className="flex items-center justify-between" data-id="element-1417">
          <p className="text-gray-600" data-id="element-1418">
            Verwaltung und Organisation des Gewürzlagers
          </p>
          {step !== 'operationType' && <button onClick={handleBack} className="flex items-center gap-2 text-gray-600 hover:text-gray-800" data-id="element-1419">
              <ArrowLeftIcon size={16} data-id="element-1420" />
              <span data-id="element-1421">Zurück</span>
            </button>}
          {step === 'operationType' && onBackToOverview && <button onClick={onBackToOverview} className="flex items-center gap-2 text-gray-600 hover:text-gray-800" data-id="element-1422">
              <ArrowLeftIcon size={16} data-id="element-1423" />
              <span data-id="element-1424">Zurück zur Übersicht</span>
            </button>}
        </div>
      </div>
      {/* Step 1: Operation Type Selection */}
      {step === 'operationType' && <div className="bg-white rounded-xl shadow-md p-8" data-id="element-1425">
          <div className="max-w-md mx-auto text-center mb-8" data-id="element-1426">
            <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1427">
              Lagervorgang auswählen
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-1428">
              Bitte wählen Sie, ob Sie eine Einlagerung oder Auslagerung
              durchführen möchten.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto" data-id="element-1429">
            <div onClick={() => handleOperationTypeSelect('storage')} className="bg-white border-2 border-green-200 hover:border-green-400 rounded-xl p-6 text-center cursor-pointer transition-all hover:shadow-md" data-id="element-1430">
              <div className="bg-green-100 p-4 rounded-full inline-flex mb-4" data-id="element-1431">
                <PackagePlusIcon size={48} className="text-green-600" data-id="element-1432" />
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1433">
                Einlagerung
              </h4>
              <p className="text-gray-600" data-id="element-1434">
                Gewürze ins Lager einlagern und Lagerplätze zuweisen.
              </p>
            </div>
            <div onClick={() => handleOperationTypeSelect('retrieval')} className="bg-white border-2 border-amber-200 hover:border-amber-400 rounded-xl p-6 text-center cursor-pointer transition-all hover:shadow-md" data-id="element-1435">
              <div className="bg-amber-100 p-4 rounded-full inline-flex mb-4" data-id="element-1436">
                <PackageXIcon size={48} className="text-amber-600" data-id="element-1437" />
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1438">
                Auslagerung
              </h4>
              <p className="text-gray-600" data-id="element-1439">
                Gewürze aus dem Lager entnehmen und für die Produktion
                bereitstellen.
              </p>
            </div>
          </div>
        </div>}
      {/* Step 2: Storage Type Selection */}
      {step === 'storageType' && <div className="bg-white rounded-xl shadow-md p-8" data-id="element-1440">
          <div className="max-w-md mx-auto text-center mb-8" data-id="element-1441">
            <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1442">
              Art der Einlagerung wählen
            </h3>
            <p className="text-gray-600 mb-6" data-id="element-1443">
              Möchten Sie Restbestände aus der Produktion oder neue Gewürze
              einlagern?
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-2xl mx-auto" data-id="element-1444">
            <div onClick={() => handleStorageTypeSelect('leftover')} className="bg-white border-2 border-blue-200 hover:border-blue-400 rounded-xl p-6 text-center cursor-pointer transition-all hover:shadow-md" data-id="element-1445">
              <div className="bg-blue-100 p-4 rounded-full inline-flex mb-4" data-id="element-1446">
                <PackageIcon size={48} className="text-blue-600" data-id="element-1447" />
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1448">
                Restbestände
              </h4>
              <p className="text-gray-600" data-id="element-1449">
                Übrig gebliebene Gewürze aus der Produktion einlagern.
              </p>
            </div>
            <div onClick={() => handleStorageTypeSelect('new')} className="bg-white border-2 border-green-200 hover:border-green-400 rounded-xl p-6 text-center cursor-pointer transition-all hover:shadow-md" data-id="element-1450">
              <div className="bg-green-100 p-4 rounded-full inline-flex mb-4" data-id="element-1451">
                <Leaf size={48} className="text-green-600" data-id="element-1452" />
              </div>
              <h4 className="text-lg font-semibold text-gray-800 mb-2" data-id="element-1453">
                Neue Gewürze
              </h4>
              <p className="text-gray-600" data-id="element-1454">
                Neu gelieferte Gewürze ins Lager einbuchen.
              </p>
            </div>
          </div>
        </div>}
      {/* Step 3: Order List */}
      {step === 'orderList' && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1455">
          <h3 className="text-xl font-bold text-gray-800 mb-4" data-id="element-1456">
            Aufträge mit Gewürzresten
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-1457">
            Wählen Sie einen Auftrag aus, um die Restbestände einzulagern
          </p>
          {/* Search bar */}
          <div className="relative mb-6" data-id="element-1458">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1459">
              <SearchIcon size={18} className="text-gray-400" data-id="element-1460" />
            </div>
            <input type="text" className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-md" placeholder="Nach Auftrag oder Gewürz suchen..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-1461" />
          </div>
          {/* Order list */}
          <div className="space-y-4" data-id="element-1462">
            {filteredOrders.length > 0 ? filteredOrders.map(order => <div key={order.id} className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer" onClick={() => handleOrderSelect(order)} data-id="element-1463">
                  <div className="flex justify-between items-start" data-id="element-1464">
                    <div data-id="element-1465">
                      <h4 className="font-medium text-gray-900" data-id="element-1466">
                        {order.recipeName}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-1467">
                        Auftrags-Nr: #{order.orderId}
                      </p>
                      <p className="text-sm text-gray-500" data-id="element-1468">
                        Datum: {order.date.toLocaleDateString()}
                      </p>
                    </div>
                    <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium" data-id="element-1469">
                      {order.remainingSpices.length} Gewürze
                    </div>
                  </div>
                  {/* Spice preview */}
                  <div className="mt-3 flex flex-wrap gap-1" data-id="element-1470">
                    {order.remainingSpices.map(spice => <span key={spice.id} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800" data-id="element-1471">
                        <Leaf size={12} className="mr-1" data-id="element-1472" />
                        {spice.name} ({spice.remaining} {spice.unit})
                      </span>)}
                  </div>
                </div>) : <div className="text-center py-10 bg-gray-50 rounded-lg" data-id="element-1473">
                <WarehouseIcon size={40} className="mx-auto text-gray-400 mb-3" data-id="element-1474" />
                <h3 className="text-lg font-medium text-gray-900 mb-1" data-id="element-1475">
                  Keine Aufträge gefunden
                </h3>
                <p className="text-gray-500" data-id="element-1476">
                  Es wurden keine Aufträge mit Gewürzresten gefunden, die Ihren
                  Suchkriterien entsprechen.
                </p>
              </div>}
          </div>
        </div>}
      {/* Step 4: Spice List */}
      {step === 'spiceList' && selectedOrder && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1477">
          <div className="mb-6" data-id="element-1478">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1479">
              Gewürzreste für {selectedOrder.recipeName}
            </h3>
            <p className="text-gray-600" data-id="element-1480">
              Wählen Sie ein Gewürz aus, um es einzulagern. Auftrag #
              {selectedOrder.orderId}
            </p>
          </div>
          <div className="space-y-4" data-id="element-1481">
            {selectedOrder.remainingSpices.map(spice => {
          const isCompleted = completedSpices.includes(spice.id);
          return <div key={spice.id} className={`border-2 rounded-lg p-4 ${isCompleted ? 'bg-green-50 border-green-200' : 'bg-white border-gray-200 hover:border-green-300 hover:shadow-md cursor-pointer'}`} onClick={() => !isCompleted && handleSpiceSelect(spice)} data-id="element-1482">
                  <div className="flex justify-between items-center" data-id="element-1483">
                    <div className="flex items-center" data-id="element-1484">
                      <div className="p-3 bg-green-100 rounded-full mr-4" data-id="element-1485">
                        <Leaf size={24} className="text-green-700" data-id="element-1486" />
                      </div>
                      <div data-id="element-1487">
                        <h4 className="text-lg font-semibold text-gray-900" data-id="element-1488">
                          {spice.name}
                        </h4>
                        <p className="text-base text-gray-600" data-id="element-1489">
                          {spice.remaining} {spice.unit}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-1490">
                          Artikel-Nr: {spice.id}
                        </p>
                      </div>
                    </div>
                    <div data-id="element-1491">
                      {isCompleted ? <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800" data-id="element-1492">
                          <CheckCircleIcon size={16} className="mr-1" data-id="element-1493" />
                          Eingelagert
                        </span> : <button className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-md text-base font-medium" onClick={e => {
                  e.stopPropagation();
                  handleSpiceSelect(spice);
                }} data-id="element-1494">
                          Einlagern
                        </button>}
                    </div>
                  </div>
                </div>;
        })}
          </div>
        </div>}
      {/* Step 5: Scan Box */}
      {step === 'scanBox' && selectedSpice && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1495">
          <div className="mb-6" data-id="element-1496">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1497">
              Box für {selectedSpice.name} scannen
            </h3>
            <p className="text-gray-600" data-id="element-1498">
              Bitte scannen Sie den Barcode der Box, in die Sie{' '}
              {selectedSpice.remaining} {selectedSpice.unit}{' '}
              {selectedSpice.name} einlagern möchten.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1499">
            <div className="flex items-center mb-4" data-id="element-1500">
              <BoxIcon size={20} className="text-gray-700 mr-2" data-id="element-1501" />
              <h4 className="font-medium text-gray-800" data-id="element-1502">Box scannen</h4>
            </div>
            <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-1503">
              <div className="relative flex-grow" data-id="element-1504">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1505">
                  <ScanLineIcon size={18} className="text-gray-400" data-id="element-1506" />
                </div>
                <input type="text" className={`pl-10 pr-4 py-2 w-full border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md`} placeholder="Box-Barcode scannen..." value={scanCode} onChange={e => setScanCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScanBox()} data-id="element-1507" />
              </div>
              <button onClick={handleScanBox} disabled={isScanning} className={`py-2 px-4 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium ${isScanning ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1508">
                {isScanning ? 'Scanne...' : 'Scannen'}
              </button>
            </div>
            {scanSuccess === true && <div className="mb-4 flex items-center text-green-600 text-sm" data-id="element-1509">
                <CheckCircleIcon size={16} className="mr-1" data-id="element-1510" />
                <span data-id="element-1511">Box erfolgreich gescannt! Box-ID: {scanCode}</span>
              </div>}
            {scanSuccess === false && <div className="mb-4 flex items-center text-red-600 text-sm" data-id="element-1512">
                <XCircleIcon size={16} className="mr-1" data-id="element-1513" />
                <span data-id="element-1514">{errorMessage}</span>
              </div>}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1515">
              <h5 className="font-medium text-gray-800 mb-3" data-id="element-1516">
                Gewürzinformationen:
              </h5>
              <div className="space-y-2" data-id="element-1517">
                <div className="flex items-start" data-id="element-1518">
                  <div className="w-32 text-gray-600" data-id="element-1519">Name:</div>
                  <div className="font-medium" data-id="element-1520">{selectedSpice.name}</div>
                </div>
                <div className="flex items-start" data-id="element-1521">
                  <div className="w-32 text-gray-600" data-id="element-1522">Artikel-Nr:</div>
                  <div className="font-medium" data-id="element-1523">{selectedSpice.id}</div>
                </div>
                <div className="flex items-start" data-id="element-1524">
                  <div className="w-32 text-gray-600" data-id="element-1525">Menge:</div>
                  <div className="font-medium" data-id="element-1526">
                    {selectedSpice.remaining} {selectedSpice.unit}
                  </div>
                </div>
                <div className="flex items-start" data-id="element-1527">
                  <div className="w-32 text-gray-600" data-id="element-1528">Hinweise:</div>
                  <div data-id="element-1529">
                    <ul className="list-disc pl-5 space-y-1 text-sm text-gray-700" data-id="element-1530">
                      {selectedSpice.notes.map((note, index) => <li key={index} data-id="element-1531">{note}</li>)}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-1532">
            <div className="flex items-start" data-id="element-1533">
              <AlertCircleIcon size={20} className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1534" />
              <p className="text-sm text-yellow-700" data-id="element-1535">
                Für Testzwecke können Sie einen beliebigen Barcode eingeben. In
                einer Produktionsumgebung würde ein echter Barcode-Scanner
                verwendet werden.
              </p>
            </div>
          </div>
        </div>}
      {/* Step 6: Fill Box */}
      {step === 'fillBox' && selectedSpice && scannedBox && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1536">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-6 shadow-sm" data-id="element-1537">
            <div className="flex items-start" data-id="element-1538">
              <div className="bg-blue-100 p-3 rounded-full mr-4 flex-shrink-0" data-id="element-1539">
                <PackageIcon size={24} className="text-blue-600" data-id="element-1540" />
              </div>
              <div data-id="element-1541">
                <h4 className="text-lg font-bold text-gray-800 mb-2" data-id="element-1542">
                  Gewürz in Box füllen
                </h4>
                <p className="text-base text-gray-700 mb-3" data-id="element-1543">
                  Bitte füllen Sie{' '}
                  <span className="font-bold text-blue-700" data-id="element-1544">
                    {selectedSpice.remaining} {selectedSpice.unit}
                  </span>{' '}
                  <span className="font-bold text-gray-800" data-id="element-1545">
                    {selectedSpice.name}
                  </span>{' '}
                  in die Box mit ID{' '}
                  <span className="font-bold text-blue-700" data-id="element-1546">{scannedBox}</span>.
                </p>
                <div className="bg-white bg-opacity-70 border border-blue-100 rounded-md p-3 text-sm text-blue-800" data-id="element-1547">
                  <div className="flex items-center" data-id="element-1548">
                    <AlertCircleIcon size={16} className="mr-2 text-blue-500" data-id="element-1549" />
                    <span data-id="element-1550">
                      Achten Sie darauf, das Gewürz vollständig und ohne Verlust
                      umzufüllen.
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className="flex justify-center mb-6" data-id="element-1551">
            <button onClick={handleFillBoxConfirm} className="py-4 px-10 bg-green-500 hover:bg-green-600 text-white rounded-lg font-medium flex items-center gap-2 w-full sm:w-auto" data-id="element-1552">
              <CheckCircleIcon size={20} data-id="element-1553" />
              <span data-id="element-1554">Bestätigen & Fortfahren</span>
            </button>
          </div>
        </div>}
      {/* Step 7: Scan Location */}
      {step === 'scanLocation' && selectedSpice && scannedBox && <div className="bg-white rounded-xl shadow-md p-6" data-id="element-1555">
          <div className="mb-6" data-id="element-1556">
            <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1557">
              Lagerplatz scannen
            </h3>
            <p className="text-gray-600" data-id="element-1558">
              Bitte scannen Sie den Barcode des Lagerplatzes, an dem die Box mit{' '}
              {selectedSpice.name} eingelagert werden soll.
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-6" data-id="element-1559">
            <div className="flex items-center mb-4" data-id="element-1560">
              <MapPinIcon size={20} className="text-gray-700 mr-2" data-id="element-1561" />
              <h4 className="font-medium text-gray-800" data-id="element-1562">Lagerplatz scannen</h4>
            </div>
            <div className="flex flex-col md:flex-row gap-3 mb-4" data-id="element-1563">
              <div className="relative flex-grow" data-id="element-1564">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-1565">
                  <ScanLineIcon size={18} className="text-gray-400" data-id="element-1566" />
                </div>
                <input type="text" className={`pl-10 pr-4 py-2 w-full border ${scanSuccess === true ? 'border-green-500 bg-green-50' : scanSuccess === false ? 'border-red-500 bg-red-50' : 'border-gray-300'} rounded-md`} placeholder="Lagerplatz-Barcode scannen..." value={scanCode} onChange={e => setScanCode(e.target.value)} onKeyDown={e => e.key === 'Enter' && handleScanLocation()} data-id="element-1567" />
              </div>
              <button onClick={handleScanLocation} disabled={isScanning} className={`py-2 px-4 bg-green-500 hover:bg-green-600 text-white rounded-md font-medium ${isScanning ? 'opacity-70 cursor-not-allowed' : ''}`} data-id="element-1568">
                {isScanning ? 'Scanne...' : 'Scannen'}
              </button>
            </div>
            {scanSuccess === true && <div className="mb-4 flex items-center text-green-600 text-sm" data-id="element-1569">
                <CheckCircleIcon size={16} className="mr-1" data-id="element-1570" />
                <span data-id="element-1571">
                  Lagerplatz erfolgreich gescannt! Lagerplatz: {scanCode}
                </span>
              </div>}
            {scanSuccess === false && <div className="mb-4 flex items-center text-red-600 text-sm" data-id="element-1572">
                <XCircleIcon size={16} className="mr-1" data-id="element-1573" />
                <span data-id="element-1574">{errorMessage}</span>
              </div>}
            <div className="bg-white rounded-lg border border-gray-200 p-4" data-id="element-1575">
              <h5 className="font-medium text-gray-800 mb-3" data-id="element-1576">
                Einlagerungsinformationen:
              </h5>
              <div className="space-y-2" data-id="element-1577">
                <div className="flex items-start" data-id="element-1578">
                  <div className="w-32 text-gray-600" data-id="element-1579">Gewürz:</div>
                  <div className="font-medium" data-id="element-1580">{selectedSpice.name}</div>
                </div>
                <div className="flex items-start" data-id="element-1581">
                  <div className="w-32 text-gray-600" data-id="element-1582">Menge:</div>
                  <div className="font-medium" data-id="element-1583">
                    {selectedSpice.remaining} {selectedSpice.unit}
                  </div>
                </div>
                <div className="flex items-start" data-id="element-1584">
                  <div className="w-32 text-gray-600" data-id="element-1585">Box-ID:</div>
                  <div className="font-medium" data-id="element-1586">{scannedBox}</div>
                </div>
              </div>
            </div>
          </div>
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4" data-id="element-1587">
            <div className="flex items-start" data-id="element-1588">
              <AlertCircleIcon size={20} className="text-yellow-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1589" />
              <p className="text-sm text-yellow-700" data-id="element-1590">
                Für Testzwecke können Sie einen beliebigen Barcode eingeben, der
                mit "L" beginnt (z.B. "L123"). In einer Produktionsumgebung
                würde ein echter Barcode-Scanner verwendet werden.
              </p>
            </div>
          </div>
        </div>}
      {/* Step: Complete */}
      {step === 'complete' && selectedSpice && <div className="bg-white rounded-xl shadow-md p-8 text-center" data-id="element-1591">
          <div className="bg-green-100 p-4 rounded-full inline-flex mb-4" data-id="element-1592">
            <CheckCircleIcon size={48} className="text-green-600" data-id="element-1593" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2" data-id="element-1594">
            Einlagerung erfolgreich!
          </h3>
          <p className="text-gray-600 mb-4" data-id="element-1595">
            {selectedSpice.name} wurde erfolgreich am Lagerplatz{' '}
            {scannedLocation} eingelagert.
          </p>
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 max-w-md mx-auto mb-6" data-id="element-1596">
            <div className="flex justify-between items-center" data-id="element-1597">
              <span className="text-gray-700" data-id="element-1598">Gewürz:</span>
              <span className="font-medium" data-id="element-1599">{selectedSpice.name}</span>
            </div>
            <div className="flex justify-between items-center mt-2" data-id="element-1600">
              <span className="text-gray-700" data-id="element-1601">Menge:</span>
              <span className="font-medium" data-id="element-1602">
                {selectedSpice.remaining} {selectedSpice.unit}
              </span>
            </div>
            <div className="flex justify-between items-center mt-2" data-id="element-1603">
              <span className="text-gray-700" data-id="element-1604">Box:</span>
              <span className="font-medium" data-id="element-1605">{scannedBox}</span>
            </div>
            <div className="flex justify-between items-center mt-2" data-id="element-1606">
              <span className="text-gray-700" data-id="element-1607">Lagerplatz:</span>
              <span className="font-medium" data-id="element-1608">{scannedLocation}</span>
            </div>
          </div>
          <p className="text-sm text-gray-500" data-id="element-1609">
            Sie werden automatisch zum nächsten Schritt weitergeleitet...
          </p>
        </div>}
    </div>;
}